import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBooking } from "../../../../context/BookingContext";
import styles from "./GripBookingFinal.module.css";
import { createPaymentOrBookingGripStaff } from "../../../../api/api";
import termsAndConditions from "../../../../constants/termsAndConditions";
import modalStyles from "../../../../constants/TermsModal.module.css";
import PayInStoreButton from "../../../../features/payment/PayInStore/PayInStoreButton";
import useAdminGuard from "../../../../hooks/useAuthGuard";

const REG_FEE = 20;
const EXP_FEE = 30;

const BookingFinal = () => {
  useAdminGuard();
  const { bookings, setBookings, pickupService, getAddressForCustomer } =
    useBooking();
  const navigate = useNavigate();
  const location = useLocation();

  const [errorMessage, setErrorMessage] = useState("");
  const termsRef = useRef(null);
  const [showingToast, setShowingToast] = useState(false);
  const clearErrTimerRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newRacketName, setNewRacketName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [isBreakdownPopupOpen, setIsBreakdownPopupOpen] = useState(false);
  const redirectTimerRef = useRef(null);

  const {
    customerId,
    bookingDate = "Not Provided",
    customerName = "N/A",
    instoreLocation = null,
    rp_id = null,
    claim_id = null,
    pickupService: statePickupService = null,
  } = location.state || {};

  // Use pickup service from navigation state if available, otherwise from context
  const effectivePickupService = statePickupService || pickupService;

  const customerBookings = useMemo(
    () => bookings.filter((b) => b.customerId === customerId),
    [bookings, customerId]
  );

  // eslint-disable-next-line no-unused-vars
  const lockedGripType = useMemo(() => {
    const b = customerBookings.find(
      (x) => x?.serviceKind === "grip" && x?.gripType
    );
    return b?.gripType || null;
  }, [customerBookings]);

  const getGripCost = (b) => Number(b.gripCost ?? 0);
  const getServiceFee = (b) =>
    Number(b.serviceFee ?? (b.gripType === "Express" ? EXP_FEE : REG_FEE));
  const getIndividualCost = (b) =>
    Number(b.totalCost ?? getGripCost(b) + getServiceFee(b));

  const getRowCost = (booking) =>
    booking.serviceKind === "grip" ? getIndividualCost(booking) : 0;

  const breakdown = useMemo(() => {
    const gripCost = customerBookings
      .filter((b) => b.serviceKind === "grip")
      .reduce((sum, b) => sum + getGripCost(b), 0);

    const gripServiceFee = customerBookings
      .filter((b) => b.serviceKind === "grip")
      .reduce((sum, b) => sum + getServiceFee(b), 0);

    let pickupFee = 0;
    if (
      effectivePickupService === "pickup" ||
      effectivePickupService === "delivery"
    )
      pickupFee = 30;
    else if (effectivePickupService === "pickup/delivery") pickupFee = 60;
    else if (
      effectivePickupService === "express-pickup" ||
      effectivePickupService === "express-delivery"
    )
      pickupFee = 50;
    else if (effectivePickupService === "express-pickup/delivery")
      pickupFee = 100;

    return {
      gripCost,
      gripServiceFee,
      pickupFee,
      grandTotal: gripCost + gripServiceFee + pickupFee,
    };
  }, [customerBookings, effectivePickupService]);

  const overallTotal = breakdown.grandTotal;

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  const showError = (msg, { focusTerms = false } = {}) => {
    setErrorMessage(msg);
    setShowingToast(true);
    if (clearErrTimerRef.current) clearTimeout(clearErrTimerRef.current);
    clearErrTimerRef.current = setTimeout(() => {
      setShowingToast(false);
      setErrorMessage("");
    }, 3200);
    if (focusTerms && termsRef.current) {
      termsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      termsRef.current.classList.add("pulseHighlight");
      setTimeout(
        () => termsRef.current?.classList.remove("pulseHighlight"),
        1600
      );
    }
  };

  const handleDelete = (index) => {
    const row = customerBookings[index];
    const updated = bookings.filter((b) => b !== row);
    setBookings(updated);
    localStorage.setItem("bookings", JSON.stringify(updated));
  };

  // ---------- Edit modal (commented out for consistency with customer side) ----------
  // const [editModalOpen, setEditModalOpen] = useState(false);
  // const [selectedBookingIndex, setSelectedBookingIndex] = useState(null);
  // const [selectedStringId, setSelectedStringId] = useState(null);
  // const [selectedStringType, setSelectedStringType] = useState("");
  // const [selectedGripId, setSelectedGripId] = useState(null);
  // const [selectedGripType, setSelectedGripType] = useState("");
  // const [totalCost, setTotalCost] = useState("");

  // const openEdit = (idx) => {
  //   const row = customerBookings[idx];
  //   setSelectedBookingIndex(idx);
  //   if (row.serviceKind === "grip") {
  //     setSelectedGripId(row.gripId || null);
  //     setSelectedGripType(row.gripType || lockedGripType || "Regular");
  //     setTotalCost(getIndividualCost(row) || 0);
  //   } else {
  //     setSelectedStringId(row.stringId || null);
  //     setSelectedStringType(row.stringType || lockedStringType || "Regular");
  //     setTotalCost(getStringCost(row) || 0);
  //   }
  //   setEditModalOpen(true);
  // };

  // const handleSaveEdit_DISABLED = () => {
  //   if (selectedBookingIndex === null) return;

  //   const original = customerBookings[selectedBookingIndex];
  //   let updated;

  //   if (original.serviceKind === "grip") {
  //     if (!selectedGripId || !selectedGripType) return;
  //     updated = bookings.map((b) =>
  //       b === original
  //         ? {
  //             ...b,
  //             gripId: selectedGripId,
  //             gripType: selectedGripType,
  //             totalCost: getIndividualCost({
  //               ...b,
  //               gripId: selectedGripId,
  //               gripType: selectedGripType,
  //             }),
  //           }
  //         : b
  //     );
  //   } else {
  //     if (!selectedStringId || !selectedStringType) return;
  //     updated = bookings.map((b) =>
  //       b === original
  //         ? {
  //             ...b,
  //             stringId: selectedStringId,
  //             stringType: selectedStringType,
  //             stringPrice: getStringCost({
  //               ...b,
  //               stringId: selectedStringId,
  //               stringType: selectedStringType,
  //             }),
  //           }
  //         : b
  //     );
  //   }

  //   setBookings(updated);
  //   localStorage.setItem("bookings", JSON.stringify(updated));
  //   setEditModalOpen(false);
  // };

  const generateUniqueCartId = () =>
    `TXN-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  const effectiveAddress = useMemo(
    () => (getAddressForCustomer ? getAddressForCustomer(customerId) : ""),
    [getAddressForCustomer, customerId]
  );

  const handleProceedToPay = async () => {
    if (!agreedToTerms) {
      showError("Please agree to the Terms & Conditions before proceeding.", {
        focusTerms: true,
      });
      return;
    }
    if (customerBookings.length === 0) {
      showError("No bookings to process.");
      return;
    }

    if (effectivePickupService !== "in-store" && !effectiveAddress?.trim()) {
      console.warn(
        `[GripBookingFinal] Blocked proceed to pay — pickup service "${effectivePickupService}" selected but no delivery address provided. Customer: ${customerId}, bookings: ${customerBookings.length}`
      );
      showError("Please add a delivery address before proceeding.");
      return;
    }

    // Validate rows by their kind
    for (const b of customerBookings) {
      if (b.serviceKind === "grip") {
        if (!b.gripId)
          return showError("Please select a grip for all grip bookings.");
        if (!b.gripType)
          return showError(
            "Please select a service type for all grip bookings."
          );
      } else {
        if (!b.stringId)
          return showError("Please select a string for all string bookings.");
        if (!b.stringType)
          return showError(
            "Please select a string type for all string bookings."
          );
      }
    }

    setIsProcessing(true);

    const cartId = generateUniqueCartId();
    const bookingRequests = customerBookings.map((b) => ({
      bigcommerce_customer_id: customerId,
      service_kind: b.serviceKind || (b.gripId ? "grip" : "string"),
      string_id: b.serviceKind === "grip" ? null : b.stringId,
      grip_id: b.serviceKind === "grip" ? b.gripId : null,
      booking_date: bookingDate,
      pickup_option: effectivePickupService,
      delivery_address:
        effectivePickupService === "in-store" ? null : effectiveAddress || null,
      racket_name: b.racketName,
      total_cost: getRowCost(b),
      cart_id: cartId,
      freeService: b.freeService || false,
      instore_location:
        effectivePickupService === "in-store" ? instoreLocation : null,
      rp_id: b.rp_id || rp_id || null,
      claim_id: b.claim_id || claim_id || null,
      customer_name: customerName,
      bc_sku: b.bc_sku || null,
      bc_name: b.bc_name || null,
      bc_brand: b.bc_brand || null,
      bc_price_inc_tax: b.bc_price_inc_tax || null,
      bc_image_url: b.bc_image_url || null,
      service_speed: b.serviceKind === "grip" ? b.gripType : b.stringType,
      service_fee: getServiceFee(b),
    }));
    try {
      const response = await createPaymentOrBookingGripStaff({
        bookings: bookingRequests,
        pickup_fee: breakdown?.pickupFee || 0,
      });
      const data = response.data;

      if (response.status === 200 || response.status === 201) {
        if (data.bookings && data.bookings.length > 0) {
          alert("Your booking has been created successfully!");
          setBookings([]);
          localStorage.removeItem("bookings");

          navigate("/grip-payment-success?freeBooking=1", {
            state: {
              bookingIds: data.bookings.map((b) => b.booking_id),
              customerName,
              totalCost: 0,
              message: "Your booking has been successfully created.",
            },
          });
        } else if (data.payment_url) {
          if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
          redirectTimerRef.current = window.setTimeout(() => {
            window.location.assign(data.payment_url);
          }, 3000);
        } else {
          showError("Something went wrong!");
        }
      } else {
        showError("Failed to create booking or process payment.");
      }
    } catch (error) {
      console.error("Booking Submission Error:", error);
      showError("Error processing bookings.");
    } finally {
      setIsProcessing(false);
    }
  };

  const openBreakdown = () => setIsBreakdownPopupOpen(true);

  const humanPickup = (v) => {
    switch (v) {
      case "pickup":
        return "Pickup Only";
      case "delivery":
        return "Delivery Only";
      case "pickup/delivery":
        return "Both Pickup & Delivery";
      case "in-store":
        return "In-Store (No Charge)";
      case "express-pickup":
        return "Express Pickup";
      case "express-delivery":
        return "Express Delivery";
      case "express-pickup/delivery":
        return "Express Pickup & Delivery";
      default:
        return v;
    }
  };

  const typeLabel = (row) => {
    const kind = row.serviceKind === "grip" ? "Grip" : "String";
    const t =
      (row.serviceKind === "grip" ? row.gripType : row.stringType) || "Regular";
    return `${t} / ${kind}`;
  };

  const itemName = (row) => {
    return row.gripName || `Grip #${row.gripId}` || "Unknown Grip";
  };

  return (
    <div className={styles.container}>
      {isProcessing && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.loader}></div>
            <p>Please wait...</p>
            <p className="subText">Your booking is being processed.</p>
          </div>
        </div>
      )}

      {errorMessage && (
        <div
          className={`${styles.errorMessage} ${
            showingToast ? styles.toastVisible : ""
          }`}
          role="status"
          aria-live="polite"
        >
          {errorMessage}
        </div>
      )}

      <h2 className={styles.title}>Booking Summary</h2>
      <p>
        <strong>Customer Name:</strong> {customerName || "N/A"}
      </p>
      <p>
        <strong>Booking Date:</strong> {bookingDate}
      </p>
      <p>
        <strong>Pickup Service:</strong> {humanPickup(effectivePickupService)}
      </p>
      {effectivePickupService !== "in-store" && (
        <p>
          <strong>Selected Address:</strong>{" "}
          {effectiveAddress || "Not Provided"}
        </p>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Racket Name</th>
              <th>Item Name</th>
              <th>Service Type</th>
              <th>Total Cost</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {customerBookings.length > 0 ? (
              customerBookings.map((row, index) => (
                <tr key={index}>
                  <td>{row.racketName || "Not Provided"}</td>
                  <td>{itemName(row)}</td>
                  <td>{typeLabel(row)}</td>
                  <td>
                    <span>
                      <span className="dirham-symbol">&#xea;</span>{" "}
                      {getRowCost(row)}
                    </span>
                  </td>
                  <td>
                    {/* <button
                      className={styles.editButton}
                      onClick={() => openEdit(index)}
                    >
                      Edit
                    </button> */}
                    <button
                      className={styles.deleteButton}
                      onClick={() => handleDelete(index)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className={styles.emptyMessage}>
                  No bookings created yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.overallCostBlock}>
        <span className={styles.overallCostText}>
          <strong>Overall Cost:</strong>{" "}
          <span className="dirham-symbol">&#xea;</span> {overallTotal}
        </span>
        <button className={styles.infoButton} onClick={openBreakdown}>
          ℹ
        </button>
      </div>

      {/* {isBreakdownPopupOpen && breakdown && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Cost Breakdown</h3>
            <p>
              <strong>Total Grip Cost:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span> {breakdown.gripCost}
            </p>
            <p>
              <strong>Total Grip Service Fee:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.gripServiceFee}
            </p>
            <p>
              <strong>Pickup/Delivery Fee:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.pickupFee}
            </p>
            <hr />
            <p>
              <strong>Grand Total:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.grandTotal}
            </p>
            <button
              className={styles.closeModalButton}
              onClick={() => setIsBreakdownPopupOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )} */}
      {isBreakdownPopupOpen && breakdown && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Cost Breakdown</h3>
              <button
                className={styles.iconCloseButton}
                onClick={() => setIsBreakdownPopupOpen(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className={styles.breakdownRow}>
              <span>Total Grip Cost:</span>
              <span className={styles.priceGroup}>
                <span className="dirham-symbol">&#xea;</span>
                {breakdown.gripCost}
              </span>
            </div>

            <div className={styles.breakdownRow}>
              <span>Total Grip Service Fee:</span>
              <span className={styles.priceGroup}>
                <span className="dirham-symbol">&#xea;</span>
                {breakdown.gripServiceFee}
              </span>
            </div>

            <div className={styles.breakdownRow}>
              <span>Pickup/Delivery Fee:</span>
              <span className={styles.priceGroup}>
                <span className="dirham-symbol">&#xea;</span>
                {breakdown.pickupFee}
              </span>
            </div>

            <hr className={styles.divider} />

            <div className={`${styles.breakdownRow} ${styles.grandTotalRow}`}>
              <span>Grand Total:</span>
              <span className={styles.priceGroup}>
                <span className="dirham-symbol">&#xea;</span>
                {breakdown.grandTotal}
              </span>
            </div>
          </div>
        </div>
      )}

      {customerBookings.length > 0 &&
        !customerBookings.some((b) => b.freeService) && (
          <div className={styles.formGroup}>
            <input
              type="text"
              value={newRacketName}
              onChange={(e) => setNewRacketName(e.target.value)}
              className={styles.input}
              placeholder="Enter Racket Name"
            />
            <button
              className={styles.addMoreButton}
              onClick={() =>
                navigate("/dashboard/grip-booking-form", {
                  state: {
                    customerId,
                    customerName,
                    bookingDate,
                    pickupService: effectivePickupService,
                    instoreLocation,
                    racketName: newRacketName,
                    isAddMore: true,
                    gripTypeConstraint: lockedGripType,
                    fromFinal: true,
                    returnPath: "/dashboard/grip-booking-final",
                    previousBookings: customerBookings,
                  },
                })
              }
            >
              Add Grip
            </button>
          </div>
        )}

      <div id="terms-section" ref={termsRef} className={styles.ackActionsRow}>
        <label className={styles.termsLabel}>
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={() => setAgreedToTerms(!agreedToTerms)}
          />
          <span>
            I agree to the{" "}
            <button
              type="button"
              onClick={() => setShowTermsPopup(true)}
              className={styles.termsLink}
            >
              Terms & Conditions
            </button>
          </span>
        </label>

        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={() => navigate(-1)}>
            Cancel
          </button>

          {overallTotal > 0 && (
            <PayInStoreButton
              bookings={customerBookings}
              customerId={customerId}
              customerName={customerName}
              pickupService={effectivePickupService}
              address={effectiveAddress}
              bookingDate={bookingDate}
              instoreLocation={instoreLocation}
              agreedToTerms={agreedToTerms}
              buttonClass={styles.outlineButton}
              onError={showError}
            />
          )}

          <button className={styles.proceedButton} onClick={handleProceedToPay}>
            {overallTotal === 0 ? "Confirm Booking" : "Proceed to Pay"}
          </button>
        </div>
      </div>

      {showTermsPopup && (
        <div className={modalStyles.termsModal}>
          <div className={modalStyles.termsModalContent}>
            <div dangerouslySetInnerHTML={{ __html: termsAndConditions }} />
            <div className={modalStyles.termsModalFooter}>
              <button
                className={modalStyles.closeButton}
                onClick={() => setShowTermsPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT MODAL (Commented out for consistency with customer side) */}
      {/* {editModalOpen && selectedBookingIndex !== null && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            ... edit modal content ...
          </div>
        </div>
      )} */}
    </div>
  );
};

export default BookingFinal;
